import React from "react";
import { useState, useMemo, useRef, useEffect } from "react";
import {
	AsYouType,
	isValidPhoneNumber,
	getCountries,
	getCountryCallingCode,
	parsePhoneNumberFromString,
	type CountryCode,
} from "libphonenumber-js";
import classNames from "classnames";

const countryNames = new Intl.DisplayNames(["en"], { type: "region" });

const ALL_COUNTRIES: {
	code: CountryCode;
	name: string;
	callingCode: string;
}[] = getCountries()
	.map((code) => ({
		code,
		name: countryNames.of(code) || code,
		callingCode: getCountryCallingCode(code),
	}))
	.sort((a, b) => a.name.localeCompare(b.name));

interface PhoneFieldProps {
	value: string; // E.164, e.g. "+919876543210"
	onChange: (value: string) => void;
	disabled?: boolean;
	defaultCountry?: CountryCode;
	inputClassName?: string;
}

function PhoneField({
	value,
	onChange,
	disabled,
	defaultCountry = "IN",
	inputClassName,
}: PhoneFieldProps) {
	const [country, setCountry] = useState<CountryCode>(defaultCountry);
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const [search, setSearch] = useState("");
	const wrapperRef = useRef<HTMLDivElement>(null);

	// What's actually shown/typed in the box - national number only, no country code.
	const [displayValue, setDisplayValue] = useState("");

	const isValid = value ? isValidPhoneNumber(value) : true;
	const selectedCountry = ALL_COUNTRIES.find((c) => c.code === country);

	// Sync FROM parent -> local display, only when the parent value doesn't
	// match what typing locally would already produce (avoids fighting the
	// user's cursor on every keystroke, but still picks up external resets).
	useEffect(() => {
		if (!value) {
			setDisplayValue("");
			return;
		}
		const parsed = parsePhoneNumberFromString(value);
		if (parsed) {
			setCountry(parsed.country || country);
			setDisplayValue(
				new AsYouType(parsed.country).input(parsed.nationalNumber),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	const filteredCountries = useMemo(() => {
		if (!search) return ALL_COUNTRIES;
		const q = search.toLowerCase();
		return ALL_COUNTRIES.filter(
			(c) =>
				c.name.toLowerCase().includes(q) ||
				c.callingCode.includes(q) ||
				c.code.toLowerCase().includes(q),
		);
	}, [search]);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (
				wrapperRef.current &&
				!wrapperRef.current.contains(e.target as Node)
			) {
				setDropdownOpen(false);
				setSearch("");
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	// Called on every keystroke with the RAW national-number text the user
	// is typing (never includes +countryCode - that's handled separately).
	const handleNumberChange = (raw: string) => {
		// Strip everything except digits before re-feeding AsYouType, so
		// deleting/adding characters never leaves stray formatting chars
		// (spaces, dashes, previous partial "+") in the source of truth.
		const digitsOnly = raw.replace(/[^\d]/g, "");

		const formatter = new AsYouType(country);
		const formattedDisplay = formatter.input(digitsOnly);
		setDisplayValue(formattedDisplay);

		if (!digitsOnly) {
			onChange("");
			return;
		}
		onChange(`+${selectedCountry?.callingCode}${digitsOnly}`);
	};

	const handleCountrySelect = (c: CountryCode) => {
		setCountry(c);
		setDropdownOpen(false);
		setSearch("");

		// Reformat whatever digits are already typed under the new country's
		// rules and recompute the outgoing E.164 with the new calling code.
		const digitsOnly = displayValue.replace(/[^\d]/g, "");
		const newCallingCode = getCountryCallingCode(c);
		const formatter = new AsYouType(c);
		setDisplayValue(formatter.input(digitsOnly));
		onChange(digitsOnly ? `+${newCallingCode}${digitsOnly}` : "");
	};

	return (
		<div className="flex flex-col min-w-[240px]" ref={wrapperRef}>
			<div
				className={classNames(
					"relative flex items-center rounded-md border px-2 shadow-sm transition-all duration-150",
					disabled
						? "border-slate-100 bg-slate-50 opacity-60 cursor-not-allowed"
						: "border-slate-200 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100",
				)}
			>
				<button
					type="button"
					disabled={disabled}
					onClick={() => setDropdownOpen((o) => !o)}
					className="flex items-center gap-1 py-1.5 pr-2 text-sm border-r border-slate-200 disabled:cursor-not-allowed"
				>
					<span>{country}</span>
					<span className="text-slate-500">
						+{selectedCountry?.callingCode}
					</span>
					<span className="text-slate-400 text-xs ml-0.5">▾</span>
				</button>

				<input
					type="tel"
					disabled={disabled}
					value={displayValue}
					placeholder="Phone number"
					onChange={(e) => handleNumberChange(e.target.value)}
					className={classNames(
						"flex-1 bg-transparent text-sm py-1.5 px-2 border-none outline-none disabled:cursor-not-allowed",
						inputClassName,
					)}
				/>

				{dropdownOpen && (
					<div className="absolute top-full left-0 z-10 mt-1 w-64 rounded-md border border-slate-200 bg-white shadow-lg">
						<input
							autoFocus
							type="text"
							placeholder="Search country..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="w-full px-3 py-2 text-sm border-b border-slate-100 outline-none"
						/>
						<ul className="max-h-56 overflow-y-auto ws-scroll">
							{filteredCountries.map((c) => (
								<li key={c.code}>
									<button
										type="button"
										onClick={() => handleCountrySelect(c.code)}
										className={classNames(
											"w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex justify-between",
											c.code === country && "bg-blue-50 text-blue-700",
										)}
									>
										<span>{c.name}</span>
										<span className="text-slate-400">+{c.callingCode}</span>
									</button>
								</li>
							))}
							{filteredCountries.length === 0 && (
								<li className="px-3 py-2 text-sm text-slate-400">No matches</li>
							)}
						</ul>
					</div>
				)}
			</div>

			{value && !isValid && (
				<span className="text-xs text-red-600 mt-1">Invalid phone number</span>
			)}
		</div>
	);
}

export default PhoneField;
